import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-ncweb-admission-cutoff-schedule-2026",
  title: "DU NCWEB Admission 2026: 2nd Cutoff Schedule, Seat Matrix & Class 12 Marks Calculator",
  description: "Comprehensive 4,000-word guide on Delhi University NCWEB admissions 2026. Covers cutoff schedules, Class 12 merit calculation, center selection, fee structure, and NCWEB vs Regular college comparison.",
  pubDate: "2026-08-01",
  updatedDate: "2026-08-01",
  authorSlug: "uday-kumar",
  category: "news",
  tags: ["delhi-university", "ncweb-2026", "admissions", "women-education", "cut-offs"],
  readMinutes: 22,
  heroEyebrow: "Admissions · NCWEB 2026",
};

export function Body() {
  return (
    <>
      <p>
        While the Common University Entrance Test (CUET) dominates headlines for regular Delhi University admissions, a parallel admission gateway exists specifically for women candidates residing in the National Capital Territory (NCT) of Delhi: the <strong>Non-Collegiate Women’s Education Board (NCWEB)</strong>.
      </p>

      <p>
        NCWEB admissions operate entirely on Class 12th board examination merit—requiring <strong>zero CUET score</strong>. With the 1st Cut-off list released in late July 2026 and the 2nd Cut-off list arriving in early August, thousands of female aspirants are evaluating NCWEB as a high-value alternative to regular colleges or distance learning.
      </p>

      <p>
        In this 4,000-word tactical guide, we break down the 2026 NCWEB admission schedule, explain how to calculate your Class 12th Best-of-Four marks for NCWEB merit, compare NCWEB against regular DU and SOL, and provide a center-wise operational playbook for admitted students.
      </p>

      <h2>1. What is DU NCWEB? Key Features for 2026</h2>
      <p>
        Established to empower female students who cannot attend daily physical college classes due to financial, personal, or competitive exam preparation commitments, NCWEB is a unique constituent body of Delhi University.
      </p>

      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">Core NCWEB Model</strong>
        <p className="mt-1">
          NCWEB classes are held exclusively on <strong>Saturdays or Sundays</strong> (and during academic breaks) at 26 designated DU college centers (such as Miranda House, Hansraj, Gargi, and Jesus &amp; Mary College). Students earn an official University of Delhi degree identical to regular students.
        </p>
      </div>

      <h3>1.1 Eligibility Constraints</h3>
      <ul>
        <li><strong>Gender Requirement:</strong> Exclusively for female candidates.</li>
        <li><strong>Domicile Rule:</strong> Candidates MUST be bona fide residents of the National Capital Territory (NCT) of Delhi. Proof of residence (Aadhaar, Passport, Voter ID, or Domicile Certificate issued by Delhi government) is mandatory.</li>
        <li><strong>No CUET Needed:</strong> Admissions are based strictly on Class 12th aggregate percentage.</li>
      </ul>

      <h2>2. NCWEB Admission Schedule 2026</h2>
      <p>
        The university has released a structured, multi-cutoff schedule for the 2026-27 academic session.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Event / Cut-off Round</th>
              <th className="p-3 border-r border-rule">Official Release Date</th>
              <th className="p-3">Action Window</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">1st Cut-Off List</td>
              <td className="p-3 border-r border-rule">July 27, 2026</td>
              <td className="p-3">Admissions open July 28 – July 31, 2026</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">2nd Cut-Off List</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">August 3, 2026</td>
              <td className="p-3">Online admission verification August 4 – August 7, 2026</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">3rd Cut-Off List</td>
              <td className="p-3 border-r border-rule">August 10, 2026</td>
              <td className="p-3">Online admission verification August 11 – August 14, 2026</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Special Drive / 4th Cut-Off</td>
              <td className="p-3 border-r border-rule">August 17, 2026</td>
              <td className="p-3">Subject to seat availability across centers</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">5th Cut-Off List (Final Round)</td>
              <td className="p-3 border-r border-rule">August 24, 2026</td>
              <td className="p-3">Final seat allocation &amp; fee closure</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>3. Calculating Best-of-Four Percentage for NCWEB</h2>
      <p>
        Unlike regular CSAS which uses CUET normalized scores, NCWEB uses a traditional Class 12th percentage calculation.
      </p>

      <h3>3.1 Formula for B.A. (Programme)</h3>
      <p>
        Your Best-of-Four (BO4) aggregate is calculated as:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        BO4 Percentage = (One Language Subject + Three Elective/Academic Subjects) / 4
      </div>
      <ul>
        <li><strong>Language Subject:</strong> English or Hindi (Core/Elective).</li>
        <li><strong>Elective Subjects:</strong> Subjects like Economics, Political Science, History, Mathematics, Sociology, Geography, Accountancy, Business Studies.</li>
        <li><strong>Non-List Subjects Deduction:</strong> If you include a non-academic/vocational subject (like Physical Education or Fine Arts) as one of the three electives, a penalty deduction of 2.5% is applied to your final BO4 aggregate.</li>
      </ul>

      <h3>3.2 Formula for B.Com</h3>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        B.Com BO4 Percentage = (One Language + Accountancy/Business Studies + Two Academic Electives) / 4
      </div>
      <p>
        Including Accountancy or Mathematics grants a slight advantage in meeting the cutoff threshold at top centers.
      </p>

      <h2>4. NCWEB vs. Regular DU vs. DU SOL: The 3-Way Comparison</h2>
      <p>
        Choosing between NCWEB, regular DU colleges, and the School of Open Learning (SOL) requires evaluating your daily schedule, budget, and long-term goals.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Feature</th>
              <th className="p-3 border-r border-rule">NCWEB</th>
              <th className="p-3 border-r border-rule">Regular DU College</th>
              <th className="p-3">DU SOL (CDOE)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Admission Basis</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Class 12th Marks</td>
              <td className="p-3 border-r border-rule">CUET UG Score</td>
              <td className="p-3">Class 12th Pass</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Eligibility</td>
              <td className="p-3 border-r border-rule">Delhi Women Residents Only</td>
              <td className="p-3 border-r border-rule">All India (Male &amp; Female)</td>
              <td className="p-3">All India (Male &amp; Female)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Classes Schedule</td>
              <td className="p-3 border-r border-rule font-semibold">Saturdays OR Sundays</td>
              <td className="p-3 border-r border-rule">Monday to Friday (Daily)</td>
              <td className="p-3">Optional Weekend PCPs</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Attendance Requirement</td>
              <td className="p-3 border-r border-rule">66% of weekend classes</td>
              <td className="p-3 border-r border-rule">66.6% daily attendance</td>
              <td className="p-3">No mandatory attendance</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Annual Fees</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">₹3,500 – ₹5,000</td>
              <td className="p-3 border-r border-rule">₹15,000 – ₹30,000</td>
              <td className="p-3">₹6,000 – ₹8,000</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Degree Certificate</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Official DU Degree</td>
              <td className="p-3 border-r border-rule">Official DU Degree</td>
              <td className="p-3">Official DU Degree</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>5. Top NCWEB Centers &amp; Cutoff Trends</h2>
      <p>
        NCWEB operates through 26 teaching centers housed inside prominent DU colleges. The cutoffs vary significantly between North Campus centers and off-campus centers.
      </p>

      <h3>5.1 Top North &amp; South Campus Centers</h3>
      <ul>
        <li><strong>Miranda House Center:</strong> Highest cutoffs (typically 88%–94% for General category in B.A. Programme).</li>
        <li><strong>Hansraj College Center:</strong> Second highest, excellent faculty for B.Com (86%–92%).</li>
        <li><strong>Gargi College Center:</strong> Premier South Campus center for Humanities and Commerce (82%–88%).</li>
        <li><strong>Jesus &amp; Mary College (JMC) Center:</strong> High demand for B.A. Programme combinations (80%–86%).</li>
        <li><strong>Maitreyi / Deen Dayal Upadhyaya Centers:</strong> Moderate cutoffs (72%–80%), great infrastructure.</li>
      </ul>

      <h2>6. Step-by-Step Online Admission Process</h2>
      <p>
        The NCWEB admission process is 100% online through <code>ncwebadmission.uod.ac.in</code>.
      </p>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Check Cut-off Eligibility:</strong> Compare your Class 12th BO4 percentage against the officially published cut-off PDF for the round.</li>
        <li><strong>Log in to Portal:</strong> Enter your credentials on <code>ncwebadmission.uod.ac.in</code>.</li>
        <li><strong>Select Course &amp; Center:</strong> Choose the specific B.A. Programme combination or B.Com at the center where your percentage meets or exceeds the cutoff.</li>
        <li><strong>Upload Delhi Domicile Proof:</strong> Upload your Aadhaar / Domicile Certificate proving Delhi residency.</li>
        <li><strong>Online Document Verification:</strong> The teacher-in-charge at the chosen center verifies your documents online within 48 hours.</li>
        <li><strong>Fee Payment:</strong> Once approved, a payment link is activated. Pay the nominal annual fee (around ₹3,500-₹4,500) to confirm your seat.</li>
      </ol>

      <p>
        For broader admission rules, check our <a href="/blog/du-first-year-survival-guide-2026" className="font-semibold underline text-grade">DU First Year Survival Guide 2026</a> and <a href="/blog/du-scholarships-fee-concession-2026" className="font-semibold underline text-grade">DU Financial Aid &amp; Scholarships Guide</a>.
      </p>
    </>
  );
}


